import {
  ProviderProfilePropertyList,
  SelectInput,
  TextareaInput,
  TextInput,
  TimeInput,
  WizardModal,
  type WizardModalProps
} from '@app/components'
import {
  getSocialErrorMessage,
  normalizeSocialUrl,
  SOCIAL_NETWORKS,
  showMessagesNotifications,
  useProviderProfile,
  useUpdateProviderProfile,
  weekDays
} from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  type SugarOperatingDay,
  type SugarProviderProfile,
  type SugarProviderProfileUpdate,
  type SugarSocialMedia
} from '@app/types'
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  createStyles,
  Group,
  Input,
  Stack,
  Stepper,
  Table,
  Text,
  useMantineTheme
} from '@mantine/core'
import {
  IconAddressBook,
  IconCalendarClock,
  IconCheck,
  IconChecklist,
  IconChecks,
  IconChevronLeft,
  IconChevronRight,
  IconHelp,
  IconInfoCircle,
  IconSchool,
  IconX
} from '@tabler/icons-react'
import { useForm, yupResolver } from '@mantine/form'
import { chunk, compact, filter, find, map, range } from 'lodash'
import { type FC, useCallback, useEffect } from 'react'
import * as yup from 'yup'

export enum StepperSteps {
  'INSTRUCTIONS',
  'GENERAL',
  'CONTACT',
  'CURRICULUM',
  'SCHEDULE',
  'CONFIRM'
}

type GeneralFormData = Pick<
  SugarProviderProfileUpdate,
  'contactEmail' | 'contactFirstName' | 'contactLastName' | 'contactPhone' | 'programDescription' | 'website'
> & {
  socialMedia: SugarSocialMedia
}
type CurriculumFormData = Pick<
  SugarProviderProfileUpdate,
  'curriculum' | 'financialAssistanceAvailable' | 'languagesSpoken'
>
type ScheduleFormData = Pick<SugarProviderProfileUpdate, 'programSeason'> & {
  operatingDays: (SugarOperatingDay & { enabled: boolean })[]
}

const dayIndexes = range(0, 7)
const SOCIAL_PLACEHOLDERS: Record<keyof SugarSocialMedia, string> = {
  facebook: '@handle or facebook.com/YourPage',
  instagram: '@handle or instagram.com/yourhandle',
  linkedin: 'https://www.linkedin.com/company/your-org',
  x: '@handle or x.com/yourhandle'
}
const useStyles = createStyles(() => ({
  fitWidth: {
    width: 1,
    whiteSpace: 'nowrap'
  }
}))

export interface ProviderProfileModalProps extends Omit<
  WizardModalProps,
  'children' | 'isChanged' | 'isLoading' | 'stepCount' | 'title'
> {
  initialStep?: StepperSteps
}

export const ProviderProfileModal: FC<ProviderProfileModalProps> = ({ initialStep, onClose, opened, ...props }) => {
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const { options, siteId } = useSugarContext()
  const { data: providerProfile, isFetching: isFetchingroviderProfile } = useProviderProfile()
  const { isUpdatingProviderProfile, updateProviderProfile } = useUpdateProviderProfile()
  const getGeneralFormInitialValues = useCallback(
    (data?: SugarProviderProfile): GeneralFormData => ({
      contactEmail: data?.contactEmail ?? '',
      contactFirstName: data?.contactFirstName ?? '',
      contactLastName: data?.contactLastName ?? '',
      contactPhone: data?.contactPhone ?? '',
      programDescription: data?.programDescription ?? '',
      socialMedia: {
        facebook: data?.socialMedia?.facebook ?? '',
        instagram: data?.socialMedia?.instagram ?? '',
        linkedin: data?.socialMedia?.linkedin ?? '',
        x: data?.socialMedia?.x ?? ''
      },
      website: data?.website ?? ''
    }),
    []
  )

  const generalForm = useForm<GeneralFormData>({
    initialValues: getGeneralFormInitialValues(providerProfile),
    validate: yupResolver(
      yup.object().shape({
        contactEmail: yup.string().trim().email('Invalid contact email', true),
        contactFirstName: yup.string(),
        contactLastName: yup.string(),
        contactPhone: yup.string(),
        programDescription: yup.string(),
        socialMedia: yup.object().shape({
          facebook: yup.string().test({
            name: 'facebook',
            test: (value, ctx) => {
              const { error } = normalizeSocialUrl('facebook', value ?? '')
              return error ? ctx.createError({ message: getSocialErrorMessage('facebook', error) }) : true
            }
          }),
          instagram: yup.string().test({
            name: 'instagram',
            test: (value, ctx) => {
              const { error } = normalizeSocialUrl('instagram', value ?? '')
              return error ? ctx.createError({ message: getSocialErrorMessage('instagram', error) }) : true
            }
          }),
          linkedin: yup.string().test({
            name: 'linkedin',
            test: (value, ctx) => {
              const { error } = normalizeSocialUrl('linkedin', value ?? '')
              return error ? ctx.createError({ message: getSocialErrorMessage('linkedin', error) }) : true
            }
          }),
          x: yup.string().test({
            name: 'x',
            test: (value, ctx) => {
              const { error } = normalizeSocialUrl('x', value ?? '')
              return error ? ctx.createError({ message: getSocialErrorMessage('x', error) }) : true
            }
          })
        }),
        website: yup.string().trim().url('Invalid website')
      })
    ),
    validateInputOnBlur: true,
    validateInputOnChange: true
  })
  const getCurriculumFormInitialValues = useCallback(
    (data?: SugarProviderProfile): CurriculumFormData => ({
      curriculum: data?.curriculum ? compact(data.curriculum) : [],
      financialAssistanceAvailable: data?.financialAssistanceAvailable
        ? compact(data.financialAssistanceAvailable)
        : [],
      languagesSpoken: data?.languagesSpoken ? compact(data.languagesSpoken) : []
    }),
    []
  )
  const curriculumForm = useForm<CurriculumFormData>({
    initialValues: getCurriculumFormInitialValues(providerProfile),
    validate: yupResolver(
      yup.object().shape({
        curriculum: yup.array().of(yup.string()),
        financialAssistanceAvailable: yup.array().of(yup.string()),
        languagesSpoken: yup.array().of(yup.string())
      })
    ),
    validateInputOnBlur: true,
    validateInputOnChange: true
  })
  const getScheduleFormInitialValues = useCallback(
    (data?: SugarProviderProfile): ScheduleFormData => ({
      operatingDays: map(dayIndexes, (dayIndex) => {
        const operatingDay = find(data?.operatingDays, ['dayIndex', dayIndex])

        return {
          dayIndex,
          enabled: Boolean(operatingDay),
          endTime: operatingDay?.endTime ?? '',
          startTime: operatingDay?.startTime ?? ''
        }
      }),
      programSeason: data?.programSeason ?? ''
    }),
    []
  )
  const scheduleForm = useForm<ScheduleFormData>({
    initialValues: getScheduleFormInitialValues(providerProfile),
    validate: yupResolver(
      yup.object().shape({
        operatingDays: yup.array().of(
          yup.object().shape({
            dayIndex: yup.number().oneOf(dayIndexes),
            enabled: yup.boolean(),
            endTime: yup
              .string()
              .when(['enabled'], ([enabled], schema) => (enabled ? schema.required('Required') : schema)),
            startTime: yup
              .string()
              .when(['enabled'], ([enabled], schema) => (enabled ? schema.required('Required') : schema))
          })
        ),
        programSeason: yup.string()
      })
    ),
    validateInputOnBlur: true,
    validateInputOnChange: true
  })

  const curriculumFormIsDirty = curriculumForm.isDirty()
  const curriculumFormIsValid = curriculumForm.isValid()
  const generalFormIsDirty = generalForm.isDirty()
  const generalFormIsValid = generalForm.isValid()
  const scheduleFormIsDirty = scheduleForm.isDirty()
  const scheduleFormIsValid = scheduleForm.isValid()
  const generalCoreValid = !('programDescription' in generalForm.errors)
  const contactValid =
    !['contactFirstName', 'contactLastName', 'contactEmail', 'contactPhone', 'website'].some(
      (k) => k in generalForm.errors
    ) && !Object.keys(generalForm.errors).some((k) => k.startsWith('socialMedia'))
  const isDirty = curriculumFormIsDirty || generalFormIsDirty || scheduleFormIsDirty
  const isValid = curriculumFormIsValid && generalFormIsValid && scheduleFormIsValid
  const isBusy = isUpdatingProviderProfile || isFetchingroviderProfile

  const resetForms = useCallback(
    (data?: SugarProviderProfile) => {
      const curriculumFormData = getCurriculumFormInitialValues(data)
      const generalFormData = getGeneralFormInitialValues(data)
      const scheduleFormData = getScheduleFormInitialValues(data)

      curriculumForm.setValues(curriculumFormData)
      curriculumForm.resetDirty(curriculumFormData)
      generalForm.setValues(generalFormData)
      generalForm.resetDirty(generalFormData)
      scheduleForm.setValues(scheduleFormData)
      scheduleForm.resetDirty(scheduleFormData)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const handleClose = useCallback(
    (data?: SugarProviderProfile) => {
      resetForms(data)
      onClose()
    },
    [onClose, resetForms]
  )

  useEffect(() => {
    resetForms(providerProfile)
  }, [providerProfile, resetForms])

  return (
    <WizardModal
      closeOnClickOutside
      closeOnEscape
      fullScreen
      isChanged={isDirty}
      isLoading={isBusy}
      onClose={handleClose}
      opened={opened}
      stepCount={6}
      stepperProps={{
        size: 'sm'
      }}
      title='Update Provider Profile'
      {...props}
    >
      {({ setStep, step }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (opened && initialStep !== undefined) {
            setStep(initialStep)
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [opened])

        return [
          <Stepper.Step
            allowStepSelect
            color={step > StepperSteps.INSTRUCTIONS ? 'green' : 'brand-dark-blue'}
            completedIcon={
              <IconCheck
                size={18}
                stroke={3}
              />
            }
            key={StepperSteps.INSTRUCTIONS}
            icon={<IconHelp size={18} />}
            label='Instructions'
          >
            <Alert radius='lg'>
              <Text size='sm'>
                Use the following pages to make any adjustments to information found on DPP&apos;s Provider Search tool:{' '}
                <Anchor href='https://find.dpp.org/welcome/'>https://find.dpp.org/welcome</Anchor>.
              </Text>
            </Alert>
            <Group
              mt='xl'
              position='right'
              spacing='xs'
            >
              <Button
                onClick={() => setStep(StepperSteps.GENERAL)}
                rightIcon={<IconChevronRight size={18} />}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>,
          <Stepper.Step
            allowStepSelect
            color={!generalCoreValid ? 'red' : step > StepperSteps.GENERAL ? 'green' : 'brand-dark-blue'}
            completedIcon={
              generalCoreValid ? (
                <IconCheck
                  size={18}
                  stroke={3}
                />
              ) : (
                <IconX
                  size={18}
                  stroke={3}
                />
              )
            }
            icon={<IconInfoCircle size={18} />}
            key={StepperSteps.GENERAL}
            label='General'
          >
            <TextInput
              disabled={isBusy}
              label='Program Name'
              readOnly
              value={providerProfile?.programName ?? ''}
              variant='unstyled'
            />
            <TextareaInput
              disabled={isBusy}
              label='Program Description'
              {...generalForm.getInputProps('programDescription')}
            />
            <Group
              mt='xl'
              position='apart'
              spacing='xs'
            >
              <Button
                disabled={isBusy}
                leftIcon={<IconChevronLeft size={18} />}
                onClick={() => setStep(StepperSteps.INSTRUCTIONS)}
              >
                Prev
              </Button>
              <Button
                disabled={!generalCoreValid || isBusy}
                onClick={() => setStep(StepperSteps.CONTACT)}
                rightIcon={<IconChevronRight size={18} />}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>,
          <Stepper.Step
            allowStepSelect={generalCoreValid}
            color={!contactValid ? 'red' : step > StepperSteps.CONTACT ? 'green' : 'brand-dark-blue'}
            completedIcon={
              contactValid ? (
                <IconCheck
                  size={18}
                  stroke={3}
                />
              ) : (
                <IconX
                  size={18}
                  stroke={3}
                />
              )
            }
            icon={<IconAddressBook size={18} />}
            key={StepperSteps.CONTACT}
            label='Contact'
          >
            <Group
              align='start'
              grow
            >
              <TextInput
                disabled={isBusy}
                label='Contact First Name'
                {...generalForm.getInputProps('contactFirstName')}
              />
              <TextInput
                disabled={isBusy}
                label='Contact Last Name'
                {...generalForm.getInputProps('contactLastName')}
              />
            </Group>
            <Group
              align='start'
              grow
            >
              <TextInput
                disabled={isBusy}
                label='Contact Email'
                {...generalForm.getInputProps('contactEmail')}
              />
              <TextInput
                disabled={isBusy}
                label='Contact Phone'
                {...generalForm.getInputProps('contactPhone')}
              />
            </Group>
            <TextInput
              disabled={isBusy}
              label='School Website'
              placeholder='https://'
              {...generalForm.getInputProps('website')}
            />
            {SOCIAL_NETWORKS.map(({ network, label, Icon }) => {
              const value: string = generalForm.values.socialMedia?.[network] ?? ''
              const { canonical, error } = normalizeSocialUrl(network, value)
              const showCheck = value.trim() !== '' && error === null

              return (
                <TextInput
                  aria-label={label}
                  description={
                    showCheck && canonical !== '' ? (
                      <Text
                        c='dimmed'
                        size='xs'
                      >
                        Will be saved as: {canonical}
                      </Text>
                    ) : undefined
                  }
                  disabled={isBusy}
                  icon={
                    <Icon
                      aria-hidden
                      size={18}
                    />
                  }
                  key={network}
                  label={label}
                  placeholder={SOCIAL_PLACEHOLDERS[network]}
                  rightSection={
                    showCheck ? (
                      <IconCheck
                        aria-hidden
                        color={theme.colors.green[6]}
                        size={18}
                      />
                    ) : null
                  }
                  {...generalForm.getInputProps(`socialMedia.${network}`)}
                />
              )
            })}
            <Group
              mt='xl'
              position='apart'
              spacing='xs'
            >
              <Button
                disabled={isBusy}
                leftIcon={<IconChevronLeft size={18} />}
                onClick={() => setStep(StepperSteps.GENERAL)}
              >
                Prev
              </Button>
              <Button
                disabled={!contactValid || isBusy}
                onClick={() => setStep(StepperSteps.CURRICULUM)}
                rightIcon={<IconChevronRight size={18} />}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>,
          <Stepper.Step
            allowStepSelect={generalFormIsValid}
            color={!curriculumFormIsValid ? 'red' : step > StepperSteps.CURRICULUM ? 'green' : 'brand-dark-blue'}
            completedIcon={
              curriculumFormIsValid ? (
                <IconCheck
                  size={18}
                  stroke={3}
                />
              ) : (
                <IconX
                  size={18}
                  stroke={3}
                />
              )
            }
            icon={<IconSchool size={18} />}
            key={StepperSteps.CURRICULUM}
            label='Curriculum'
          >
            <Checkbox.Group
              label='Curriculum'
              mb='xs'
              {...curriculumForm.getInputProps('curriculum')}
            >
              <Group
                align='start'
                grow
                spacing='xs'
              >
                {options?.curriculum.length
                  ? map(chunk(options.curriculum, Math.ceil(options.curriculum.length / 3)), (options, idx) => (
                      <Stack
                        key={`chunk-${idx}`}
                        spacing={0}
                      >
                        {map(options, (option) => (
                          <Checkbox
                            color='brand-dark-blue'
                            disabled={isBusy}
                            key={option.value}
                            mt='xs'
                            {...option}
                          />
                        ))}
                      </Stack>
                    ))
                  : null}
              </Group>
            </Checkbox.Group>
            <Checkbox.Group
              label='Languages Spoken'
              mb='xs'
              {...curriculumForm.getInputProps('languagesSpoken')}
            >
              <Group
                align='start'
                grow
                spacing='xs'
              >
                {options?.languagesSpoken.length
                  ? map(
                      chunk(options.languagesSpoken, Math.ceil(options.languagesSpoken.length / 3)),
                      (options, idx) => (
                        <Stack
                          key={`chunk-${idx}`}
                          spacing={0}
                        >
                          {map(options, (option) => (
                            <Checkbox
                              color='brand-dark-blue'
                              disabled={isBusy}
                              key={option.value}
                              mt='xs'
                              {...option}
                            />
                          ))}
                        </Stack>
                      )
                    )
                  : null}
              </Group>
            </Checkbox.Group>
            <Checkbox.Group
              label='Financial Assistance Available'
              mb='xs'
              {...curriculumForm.getInputProps('financialAssistanceAvailable')}
            >
              <Group
                align='start'
                grow
                spacing='xs'
              >
                {options?.programSubsidiesAccepted.length
                  ? map(
                      chunk(options.programSubsidiesAccepted, Math.ceil(options.programSubsidiesAccepted.length / 3)),
                      (options, idx) => (
                        <Stack
                          key={`chunk-${idx}`}
                          spacing={0}
                        >
                          {map(options, (option) => (
                            <Checkbox
                              color='brand-dark-blue'
                              disabled={isBusy}
                              key={option.value}
                              mt='xs'
                              {...option}
                            />
                          ))}
                        </Stack>
                      )
                    )
                  : null}
              </Group>
            </Checkbox.Group>
            <Group
              mt='xl'
              position='apart'
              spacing='xs'
            >
              <Button
                disabled={isBusy}
                leftIcon={<IconChevronLeft size={18} />}
                onClick={() => setStep(StepperSteps.CONTACT)}
              >
                Prev
              </Button>
              <Button
                disabled={!curriculumFormIsValid || isBusy}
                onClick={() => setStep(StepperSteps.SCHEDULE)}
                rightIcon={<IconChevronRight size={18} />}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>,
          <Stepper.Step
            allowStepSelect={generalFormIsValid && curriculumFormIsValid}
            color={!scheduleFormIsValid ? 'red' : step > StepperSteps.SCHEDULE ? 'green' : 'brand-dark-blue'}
            completedIcon={
              scheduleFormIsValid ? (
                <IconCheck
                  size={18}
                  stroke={3}
                />
              ) : (
                <IconX
                  size={18}
                  stroke={3}
                />
              )
            }
            icon={<IconCalendarClock size={18} />}
            key={StepperSteps.SCHEDULE}
            label='Schedule'
          >
            <SelectInput
              data={options?.programSeason ?? []}
              disabled={isBusy}
              label='Program Season'
              {...scheduleForm.getInputProps('programSeason')}
            />
            <Input.Wrapper label='Operating Hours'>
              <Table
                mt={4}
                withBorder
              >
                <thead>
                  <tr>
                    <th className={classes.fitWidth}>&nbsp;</th>
                    <th>Day</th>
                    <th className={classes.fitWidth}>Start</th>
                    <th className={classes.fitWidth}>End</th>
                  </tr>
                </thead>
                <tbody>
                  {map(weekDays, (weekDay, dayIndex) => {
                    const checked = scheduleForm.values.operatingDays[dayIndex].enabled
                    const enabledKey = `operatingDays.${dayIndex}.enabled`
                    const endKey = `operatingDays.${dayIndex}.endTime`
                    const startKey = `operatingDays.${dayIndex}.startTime`

                    return (
                      <tr key={weekDay}>
                        <td
                          valign='top'
                          className={classes.fitWidth}
                        >
                          <Checkbox
                            color='brand-dark-blue'
                            checked={checked}
                            disabled={isBusy}
                            onChange={(ev) => {
                              const nextValue = ev.currentTarget.checked

                              scheduleForm.clearFieldError(endKey)
                              scheduleForm.clearFieldError(startKey)
                              scheduleForm.setFieldValue(enabledKey, nextValue)

                              if (!nextValue) {
                                scheduleForm.setFieldValue(startKey, '')
                                scheduleForm.setFieldValue(endKey, '')
                              }
                            }}
                          />
                        </td>
                        <td valign='top'>
                          <Text size='sm'>{weekDays[dayIndex]}</Text>
                        </td>
                        <td
                          className={classes.fitWidth}
                          valign='top'
                        >
                          <TimeInput
                            disabled={!checked || isBusy}
                            mb={0}
                            size='sm'
                            w={120}
                            {...scheduleForm.getInputProps(startKey)}
                          />
                        </td>
                        <td
                          className={classes.fitWidth}
                          valign='top'
                        >
                          <TimeInput
                            disabled={!checked || isBusy}
                            mb={0}
                            size='sm'
                            w={120}
                            {...scheduleForm.getInputProps(endKey)}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </Input.Wrapper>
            <Group
              mt='xl'
              position='apart'
              spacing='xs'
            >
              <Button
                disabled={isBusy}
                leftIcon={<IconChevronLeft size={18} />}
                onClick={() => setStep(StepperSteps.CURRICULUM)}
              >
                Prev
              </Button>
              <Button
                disabled={!scheduleFormIsValid || isBusy}
                onClick={() => setStep(StepperSteps.CONFIRM)}
                rightIcon={<IconChevronRight size={18} />}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>,
          <Stepper.Step
            allowStepSelect={generalFormIsValid && curriculumFormIsValid && scheduleFormIsValid}
            color='brand-dark-blue'
            icon={<IconChecklist size={18} />}
            key={StepperSteps.CONFIRM}
            label='Confirm'
            loading={isBusy}
          >
            <Alert radius='lg'>
              Review your changes below. If everything is correct, press Save to submit. If you need to change anything,
              you can go back to previous steps by clicking on the step above, or by clicking Previous.
            </Alert>
            {providerProfile && (
              <ProviderProfilePropertyList
                my='xl'
                providerProfile={{
                  ...providerProfile,
                  ...generalForm.values,
                  ...curriculumForm.values,
                  ...scheduleForm.values
                }}
              />
            )}
            <Group
              position='apart'
              spacing='xs'
            >
              <Button
                disabled={isBusy}
                leftIcon={<IconChevronLeft size={18} />}
                onClick={() => setStep(StepperSteps.SCHEDULE)}
              >
                Prev
              </Button>
              <Button
                color='green.8'
                disabled={!isDirty || !isValid || isBusy}
                leftIcon={<IconChecks size={18} />}
                onClick={async () => {
                  if (siteId) {
                    const { operatingDays, programSeason } = scheduleForm.values
                    const { socialMedia: currentSocial, ...generalRest } = generalForm.values

                    const initialSocial: SugarSocialMedia = providerProfile?.socialMedia ?? {
                      facebook: '',
                      instagram: '',
                      linkedin: '',
                      x: ''
                    }
                    const socialMediaPayload: Partial<SugarSocialMedia> = {}
                    for (const { network } of SOCIAL_NETWORKS) {
                      const before = initialSocial[network]
                      const after = (currentSocial[network] ?? '').trim()
                      if (after === '' && before === '') continue
                      if (after === '' && before !== '') {
                        socialMediaPayload[network] = ''
                        continue
                      }
                      const { canonical } = normalizeSocialUrl(network, after)
                      if (canonical !== before) socialMediaPayload[network] = canonical
                    }

                    const update: SugarProviderProfileUpdate = {
                      ...generalRest,
                      ...curriculumForm.values,
                      operatingDays: map(
                        filter(operatingDays, 'enabled'),
                        ({ enabled, ...operatingDay }) => operatingDay
                      ),
                      programSeason,
                      siteId,
                      ...(Object.keys(socialMediaPayload).length > 0 ? { socialMedia: socialMediaPayload } : {})
                    }

                    const { data, success, messages } = await updateProviderProfile(update)

                    showMessagesNotifications(messages)

                    if (success) {
                      handleClose(data)
                      setStep(StepperSteps.INSTRUCTIONS)
                    }
                  }
                }}
              >
                Confirm & Save
              </Button>
            </Group>
          </Stepper.Step>
        ]
      }}
    </WizardModal>
  )
}
